import { useState, useMemo } from 'react'
import { ChevronLeft, ChevronRight, Plus, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { BoardWithLists, BoardCard } from '@/types'
import TaskModal from './TaskModal'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  eachHourOfInterval,
  isSameMonth,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  isToday,
  parseISO,
  startOfDay,
  getHours,
  setHours,
} from 'date-fns'

type ViewMode = 'month' | 'week' | 'day'

interface CalendarViewProps {
  board: BoardWithLists
  boardId: string
  projectName?: string
  onCardClick: (card: BoardCard) => void
}

export default function CalendarView({ board, boardId, projectName, onCardClick }: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<ViewMode>('month')
  const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false)
  const [_selectedDate, setSelectedDate] = useState<string>('')
  // _selectedDate can be used in future to pre-populate due date in TaskModal

  // Get all cards (with and without due dates for display)
  const allCards = useMemo(() => {
    const cards: (BoardCard & { listName: string })[] = []
    board.lists.forEach(list => {
      list.cards?.forEach(card => {
        cards.push({ ...card, listName: list.list_name })
      })
    })
    return cards
  }, [board.lists])

  // Get cards with due dates
  const cardsWithDates = useMemo(() => {
    return allCards.filter(card => card.due_date)
  }, [allCards])

  // Group cards by date
  const cardsByDate = useMemo(() => {
    const grouped: Record<string, (BoardCard & { listName: string })[]> = {}
    cardsWithDates.forEach(card => {
      if (card.due_date) {
        const dateKey = format(parseISO(card.due_date), 'yyyy-MM-dd')
        if (!grouped[dateKey]) {
          grouped[dateKey] = []
        }
        grouped[dateKey].push(card)
      }
    })
    return grouped
  }, [cardsWithDates])

  // Generate calendar days based on view mode
  const calendarDays = useMemo(() => {
    if (viewMode === 'month') {
      const monthStart = startOfMonth(currentDate)
      const monthEnd = endOfMonth(currentDate)
      const calendarStart = startOfWeek(monthStart)
      const calendarEnd = endOfWeek(monthEnd)
      return eachDayOfInterval({ start: calendarStart, end: calendarEnd })
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate)
      const weekEnd = endOfWeek(currentDate)
      return eachDayOfInterval({ start: weekStart, end: weekEnd })
    } else {
      return [currentDate]
    }
  }, [currentDate, viewMode])

  // Hours for day/week view
  const dayHours = useMemo(() => {
    return eachHourOfInterval({
      start: setHours(startOfDay(currentDate), 6),
      end: setHours(startOfDay(currentDate), 22),
    })
  }, [currentDate])

  const openAddTaskDialog = (dateKey: string) => {
    setSelectedDate(dateKey)
    setAddTaskDialogOpen(true)
  }

  const closeAddTaskDialog = () => {
    setAddTaskDialogOpen(false)
    setSelectedDate('')
  }

  const navigate = (direction: 'prev' | 'next') => {
    if (viewMode === 'month') {
      setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1))
    } else if (viewMode === 'week') {
      setCurrentDate(direction === 'prev' ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1))
    } else {
      setCurrentDate(direction === 'prev' ? subDays(currentDate, 1) : addDays(currentDate, 1))
    }
  }

  const goToToday = () => setCurrentDate(new Date())

  const getHeaderTitle = () => {
    if (viewMode === 'month') {
      return format(currentDate, 'MMMM yyyy')
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate)
      const weekEnd = endOfWeek(currentDate)
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`
    } else {
      return format(currentDate, 'EEEE, MMMM d, yyyy')
    }
  }

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  // Render Month View
  const renderMonthView = () => {
    const weeks = []
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7))
    }

    return (
      <div className="flex-1 flex flex-col min-h-0">
        {/* Week day headers */}
        <div className="grid grid-cols-7 border-b flex-shrink-0">
          {weekDays.map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar weeks */}
        <div className="flex-1 flex flex-col min-h-0">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="flex-1 grid grid-cols-7 min-h-[100px]">
              {week.map((day) => {
                const dateKey = format(day, 'yyyy-MM-dd')
                const dayCards = cardsByDate[dateKey] || []
                const isCurrentMonth = isSameMonth(day, currentDate)
                const isDayToday = isToday(day)

                return (
                  <div
                    key={dateKey}
                    className={`border-b border-r p-1 flex flex-col group ${
                      !isCurrentMonth ? 'bg-muted/30' : ''
                    } ${isDayToday ? 'bg-primary/5' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-1 flex-shrink-0">
                      <span className={`text-sm font-medium px-1.5 py-0.5 rounded ${
                        isDayToday
                          ? 'bg-primary text-primary-foreground'
                          : !isCurrentMonth
                            ? 'text-muted-foreground'
                            : ''
                      }`}>
                        {format(day, 'd')}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => openAddTaskDialog(dateKey)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Cards for this day */}
                    <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
                      {dayCards.slice(0, 3).map(card => (
                        <div
                          key={card.card_id}
                          className={`text-xs p-1 rounded cursor-pointer truncate ${
                            card.is_completed
                              ? 'bg-muted text-muted-foreground line-through'
                              : 'bg-primary/10 hover:bg-primary/20'
                          }`}
                          style={card.cover_color ? { borderLeft: `3px solid ${card.cover_color}` } : {}}
                          onClick={() => onCardClick(card)}
                          title={`${card.card_title} (${card.listName})`}
                        >
                          {card.card_title}
                        </div>
                      ))}
                      {dayCards.length > 3 && (
                        <div className="text-xs text-muted-foreground px-1">
                          +{dayCards.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    )
  }


  // Render Week View
  const renderWeekView = () => {
    return (
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Week day headers */}
        <div className="grid grid-cols-8 border-b flex-shrink-0">
          <div className="p-2 text-center text-sm font-medium text-muted-foreground border-r">
            Time
          </div>
          {calendarDays.map((day) => {
            const isDayToday = isToday(day)
            return (
              <div
                key={format(day, 'yyyy-MM-dd')}
                className={`p-2 text-center border-r ${isDayToday ? 'bg-primary/5' : ''}`}
              >
                <div className="text-sm font-medium text-muted-foreground">
                  {format(day, 'EEE')}
                </div>
                <div className={`text-lg font-semibold ${isDayToday ? 'text-primary' : ''}`}>
                  {format(day, 'd')}
                </div>
              </div>
            )
          })}
        </div>

        {/* Time slots */}
        <div className="flex-1 overflow-y-auto">
          {dayHours.map((hour) => (
            <div key={hour.toISOString()} className="grid grid-cols-8 border-b min-h-[60px]">
              <div className="p-2 text-xs text-muted-foreground border-r flex-shrink-0">
                {format(hour, 'h a')}
              </div>
              {calendarDays.map((day) => {
                const dateKey = format(day, 'yyyy-MM-dd')
                const dayCards = cardsByDate[dateKey] || []
                const hourCards = dayCards.filter(card => {
                  if (!card.due_date) return false
                  const cardHour = getHours(parseISO(card.due_date))
                  return cardHour === getHours(hour)
                })
                const isDayToday = isToday(day)

                return (
                  <div
                    key={`${dateKey}-${getHours(hour)}`}
                    className={`border-r p-1 group relative ${isDayToday ? 'bg-primary/5' : ''}`}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => openAddTaskDialog(dateKey)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                    {hourCards.map(card => (
                      <div
                        key={card.card_id}
                        className={`text-xs p-1 rounded cursor-pointer truncate mb-1 ${
                          card.is_completed
                            ? 'bg-muted text-muted-foreground line-through'
                            : 'bg-primary/10 hover:bg-primary/20'
                        }`}
                        style={card.cover_color ? { borderLeft: `3px solid ${card.cover_color}` } : {}}
                        onClick={() => onCardClick(card)}
                        title={card.card_title}
                      >
                        {card.card_title}
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Render Day View
  const renderDayView = () => {
    const dateKey = format(currentDate, 'yyyy-MM-dd')
    const dayCards = cardsByDate[dateKey] || []

    return (
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Day header */}
        <div className="p-4 border-b flex-shrink-0 flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold">{format(currentDate, 'EEEE')}</div>
            <div className="text-muted-foreground">{format(currentDate, 'MMMM d, yyyy')}</div>
          </div>
          <Button onClick={() => openAddTaskDialog(dateKey)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Task
          </Button>
        </div>

        {/* Time slots */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-[80px_1fr]">
            {dayHours.map((hour) => {
              const hourCards = dayCards.filter(card => {
                if (!card.due_date) return false
                const cardHour = getHours(parseISO(card.due_date))
                return cardHour === getHours(hour)
              })

              return (
                <div key={hour.toISOString()} className="contents">
                  <div className="p-2 text-sm text-muted-foreground border-r border-b text-right pr-4">
                    {format(hour, 'h a')}
                  </div>
                  <div className="border-b p-2 min-h-[80px] group relative">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => openAddTaskDialog(dateKey)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <div className="space-y-2">
                      {hourCards.map(card => (
                        <div
                          key={card.card_id}
                          className={`p-2 rounded cursor-pointer ${
                            card.is_completed
                              ? 'bg-muted text-muted-foreground'
                              : 'bg-primary/10 hover:bg-primary/20'
                          }`}
                          style={card.cover_color ? { borderLeft: `4px solid ${card.cover_color}` } : {}}
                          onClick={() => onCardClick(card)}
                        >
                          <div className={`font-medium ${card.is_completed ? 'line-through' : ''}`}>
                            {card.card_title}
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {card.due_date && format(parseISO(card.due_date), 'h:mm a')}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {card.listName}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* All day tasks (tasks without specific time) */}
        {dayCards.length > 0 && (
          <div className="border-t p-4 flex-shrink-0">
            <h3 className="font-medium mb-2">All Tasks for {format(currentDate, 'MMM d')}</h3>
            <div className="flex flex-wrap gap-2">
              {dayCards.map(card => (
                <Badge
                  key={card.card_id}
                  variant={card.is_completed ? 'secondary' : 'default'}
                  className={`cursor-pointer ${card.is_completed ? 'line-through' : ''}`}
                  onClick={() => onCardClick(card)}
                >
                  {card.card_title}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col p-4 min-h-0 overflow-hidden">
      <div className="bg-card rounded-lg border flex-1 flex flex-col min-h-0 overflow-hidden text-card-foreground">
        {/* Calendar Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold min-w-[200px]">
              {getHeaderTitle()}
            </h2>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" onClick={() => navigate('prev')}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Today
              </Button>
              <Button variant="outline" size="icon" onClick={() => navigate('next')}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* View Mode Selector */}
            <div className="flex items-center border rounded-lg overflow-hidden">
              <Button
                variant={viewMode === 'month' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode('month')}
              >
                Month
              </Button>
              <Button
                variant={viewMode === 'week' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none border-x"
                onClick={() => setViewMode('week')}
              >
                Week
              </Button>
              <Button
                variant={viewMode === 'day' ? 'default' : 'ghost'}
                size="sm"
                className="rounded-none"
                onClick={() => setViewMode('day')}
              >
                Day
              </Button>
            </div>
            <Button onClick={() => openAddTaskDialog(format(currentDate, 'yyyy-MM-dd'))}>
              <Plus className="h-4 w-4 mr-2" />
              Add Task
            </Button>
          </div>
        </div>

        {/* Calendar Content */}
        {viewMode === 'month' && renderMonthView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'day' && renderDayView()}

        {/* Legend */}
        <div className="p-3 border-t flex items-center gap-4 text-xs text-muted-foreground flex-shrink-0">
          <span className="font-medium">Lists:</span>
          {board.lists.map(list => (
            <Badge key={list.list_id} variant="outline" className="text-xs">
              {list.list_name}: {list.cards?.filter(c => c.due_date).length || 0}
            </Badge>
          ))}
          <span className="ml-auto">{cardsWithDates.length} tasks with due dates</span>
        </div>
      </div>

      {/* Add Task Modal */}
      {addTaskDialogOpen && (
        <TaskModal
          listId={board.lists?.[0]?.list_id}
          lists={board.lists}
          boardId={boardId}
          projectName={projectName}
          labels={board.labels || []}
          onClose={closeAddTaskDialog}
          mode="create"
        />
      )}
    </div>
  )
}

